/**
 * PM Platform -- Contact Center dropdown (header desktop).
 *
 * El trigger (.pm-header-contact, fila superior) y el panel
 * (.pm-header-contact__panel, renderizado al final de #header para
 * poder posicionarse debajo de la fila de navegacion completa -- ver
 * assets/pm-header-contact-dropdown.css) ya NO son el mismo contenedor
 * DOM. Por eso el estado abierto/cerrado no puede resolverse solo con
 * :hover/:focus-within (no hay ancestro comun util a distancia sin
 * cubrir la fila de navegacion con una zona invisible, descartado
 * explicitamente). Esta clase es ahora la UNICA autoridad sobre el
 * estado "is-open" del trigger, que #header:has(...) lee en CSS.
 *
 * El panel se ubica via aria-controls/id (mismo id que ya usaba el
 * markup), no requiere anidacion DOM. mouseleave programa un cierre con
 * un pequeno margen (no un algoritmo de trayectoria/menu-aim -- esto es
 * un solo trigger y un solo panel, no varias categorias) para que
 * cruzar la fila de navegacion con el cursor, en el camino de trigger a
 * panel, no cierre el dropdown a mitad de camino; entrar a cualquiera de
 * los dos cancela ese cierre.
 *
 * Toggle real por click: "is-open" (visual/ARIA) sigue siendo la unica
 * clase que CSS lee, pero ahora existe un estado adicional por instancia,
 * isPinnedOpen, que distingue "abierto transitoriamente por hover/focus"
 * de "abierto explicitamente por click". mouseleave/focusout NUNCA
 * cierran mientras isPinnedOpen es true (scheduleClose() lo comprueba
 * como unico punto de entrada) -- asi un click (venga o no de un hover
 * previo) fija el panel abierto hasta un segundo click, un click afuera,
 * o Escape. shopify:block:select sigue abriendo sin tocar isPinnedOpen
 * (apertura programatica del Theme Editor, no interaccion real).
 *
 * CLICK POR DELEGACION (esta ronda, auditoria real pre-presentacion):
 * diagnostico real en Chrome Responsive (1155px, pointer:coarse) mostro
 * la secuencia completa pointerenter/pointerdown/touchstart/pointerup/
 * pointerleave/touchend/click llegando realmente al boton visible (sin
 * overlay, sin pointer-events:none, confirmado por elementFromPoint) sin
 * que aria-expanded/is-open cambiaran -- evidencia de que ESE nodo nunca
 * tuvo el listener directo de click funcionando, sin poder confirmarse
 * la causa exacta (init temprano, root distinto, u otra) desde lectura
 * estatica. El toggle de click ya NO se adjunta directamente sobre
 * trigger dentro de initInstance: vive en el listener global de click ya
 * existente (mismo que cierra por click-afuera), que ahora ademas
 * localiza el trigger real bajo el punto de click en el momento MISMO
 * del click (event.target.closest, nunca una referencia cacheada) y
 * fuerza initInstance(root) si aun no completo -- initInstance ya es
 * idempotente (WeakSet), asi que en el caso normal (ya inicializado) esto
 * es un no-op instantaneo. Esto cierra por completo la ventana de "el
 * boton existe y es visible pero nunca se le adjunto un listener
 * funcional", sin importar la causa exacta ni el momento en que ocurrio.
 * hover/focus (mouseenter/mouseleave/focusin/focusout) siguen siendo
 * listeners directos por instancia, sin cambios -- el diagnostico no
 * implico ningun fallo ahi.
 *
 * Coordenada X del panel: la posicion horizontal real del trigger es
 * resultado de layout de contenido real (grid con tracks auto en
 * .head-mast, buscador de ancho variable, flex en .head-slot-mid) sin
 * ningun token CSS que la exponga -- confirmado por auditoria, ninguna
 * alternativa CSS-pura es fiable sin duplicar el contenido real del
 * header. Por eso, y solo para X, se mide una vez por evento relevante
 * (nunca en bucle, nunca por scroll/mousemove): criterio CENTRADO
 * (panelCenter = triggerCenter), no alineado al borde derecho. Se
 * calcula el centro real del trigger, se proyecta donde deberian caer
 * el borde izquierdo/derecho del panel (usando el ancho real medido del
 * panel) para que su centro coincida, se aplica un clamp de viewport
 * (2rem reales, leidos del font-size de :root, nunca asumidos como
 * 20px) desplazando el resultado sin deformar su ancho si no cabe, y se
 * publica la diferencia contra el borde derecho del anchor como
 * --pm-contact-panel-right-offset (mismo mecanismo JS-mide/CSS-posiciona
 * de siempre, solo cambio la formula). El eje Y sigue siendo 100%
 * estructural: este archivo nunca lee ni escribe top/bottom/height ni
 * mide .navigation-wrapper.
 */
(function () {
  "use strict";

  var CLOSE_DELAY_MS = 300;

  var initialized = new WeakSet();
  var instances = [];
  var syncFrame = null;

  function isDesktop() {
    return window.matchMedia("(min-width: 990px)").matches;
  }

  function syncHorizontalPosition(instance) {
    if (!instance.root.isConnected || !instance.anchor.isConnected) return;
    if (!isDesktop()) return;

    var anchorRect = instance.anchor.getBoundingClientRect();
    var triggerRect = instance.trigger.getBoundingClientRect();
    var panelWidth = instance.panel.getBoundingClientRect().width;

    var triggerCenter = (triggerRect.left + triggerRect.right) / 2;
    var desiredLeft = triggerCenter - panelWidth / 2;
    var desiredRight = triggerCenter + panelWidth / 2;

    // 2rem reales, no asumidos como 20px -- leidos del font-size real de
    // :root (root.style.setProperty en head-critical.liquid puede
    // escalarlo via --font-body-scale).
    var gutter = parseFloat(getComputedStyle(document.documentElement).fontSize) * 2;
    var viewportWidth = document.documentElement.clientWidth;

    var finalLeft = desiredLeft;
    var finalRight = desiredRight;

    // Clamp por desplazamiento, nunca por deformacion del ancho: si no
    // cabe centrado, se corre el conjunto izquierda/derecha en bloque.
    if (finalLeft < gutter) {
      var shiftRight = gutter - finalLeft;
      finalLeft += shiftRight;
      finalRight += shiftRight;
    }

    if (finalRight > viewportWidth - gutter) {
      var shiftLeft = finalRight - (viewportWidth - gutter);
      finalRight -= shiftLeft;
      finalLeft -= shiftLeft;
    }

    var offset = anchorRect.right - finalRight;

    instance.anchor.style.setProperty("--pm-contact-panel-right-offset", offset + "px");
  }

  function scheduleSyncAll() {
    if (syncFrame) return;
    syncFrame = requestAnimationFrame(function () {
      syncFrame = null;
      instances = instances.filter(function (instance) {
        if (!instance.root.isConnected) return false;
        syncHorizontalPosition(instance);
        return true;
      });
    });
  }

  window.addEventListener("resize", scheduleSyncAll);

  if (window.document.fonts && window.document.fonts.ready) {
    window.document.fonts.ready.then(scheduleSyncAll);
  }

  function initInstance(root) {
    if (initialized.has(root)) return;

    var trigger = root.querySelector(".pm-header-contact__trigger");
    if (!trigger) return;

    var panelId = trigger.getAttribute("aria-controls");
    var panel = panelId && document.getElementById(panelId);
    if (!panel) return;

    var anchor = panel.closest(".pm-header-contact-anchor");
    if (!anchor) return;

    initialized.add(root);

    var closeTimer = null;

    // isPinnedOpen vive en el objeto compartido (no en una variable de
    // closure suelta) para que el listener global de click-afuera pueda
    // leerlo/escribirlo sin duplicar estado.
    var instance = { root: root, trigger: trigger, panel: panel, anchor: anchor, isPinnedOpen: false, open: open, close: closeNow };
    instances.push(instance);

    function open() {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      root.classList.add("is-open");
      trigger.setAttribute("aria-expanded", "true");
      scheduleSyncAll();
    }

    function closeNow() {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      root.classList.remove("is-open");
      trigger.setAttribute("aria-expanded", "false");
    }

    function scheduleClose() {
      if (instance.isPinnedOpen) return;
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        closeTimer = null;
        root.classList.remove("is-open");
        trigger.setAttribute("aria-expanded", "false");
      }, CLOSE_DELAY_MS);
    }

    function isFocusInside() {
      return root.contains(document.activeElement) || panel.contains(document.activeElement);
    }

    function isHoveredEither() {
      return root.matches(":hover") || panel.matches(":hover");
    }

    [root, panel].forEach(function (el) {
      el.addEventListener("mouseenter", open);

      el.addEventListener("mouseleave", function () {
        if (isFocusInside()) return;
        scheduleClose();
      });

      el.addEventListener("focusin", open);

      el.addEventListener("focusout", function (event) {
        if (instance.isPinnedOpen) return;
        var next = event.relatedTarget;
        if (next && (root.contains(next) || panel.contains(next))) return;
        if (isHoveredEither()) return;
        scheduleClose();
      });

      el.addEventListener("keydown", function (event) {
        if (event.key !== "Escape") return;
        instance.isPinnedOpen = false;
        closeNow();
        trigger.focus();
      });
    });

    // Toggle real por click: ver comentario de archivo (arriba, "CLICK
    // POR DELEGACION") -- ya NO se adjunta aqui un listener directo sobre
    // trigger. El listener global de click (mas abajo en este archivo)
    // localiza el trigger real en cada click y ejecuta este mismo toggle
    // sobre la instancia (open/close ya expuestos arriba).

    // Mismo patron ya usado en assets/pm-multibanner.js: el evento se
    // despacha sobre el elemento del block real (el que lleva
    // block.shopify_attributes, dentro del panel) y hace bubble. Apertura
    // programatica del Theme Editor: no toca isPinnedOpen (no es
    // interaccion real del storefront).
    panel.addEventListener("shopify:block:select", function () {
      open();
    });

    panel.addEventListener("shopify:block:deselect", function () {
      scheduleClose();
    });
  }

  // Listener global unico (no uno por instancia): resuelve DOS
  // responsabilidades sobre el mismo click real, siempre releyendo el
  // DOM actual bajo el punto de click (nunca una referencia cacheada de
  // init):
  //
  // 1. Toggle del trigger: si el click cayo sobre (o dentro de) un
  //    .pm-header-contact__trigger real, se fuerza initInstance(root) --
  //    no-op instantaneo si ya estaba inicializado, gracias al WeakSet --
  //    y se ejecuta el mismo toggle pinned-open/close que antes vivia
  //    como listener directo (ver "CLICK POR DELEGACION" arriba).
  //
  // 2. Cierre por click-afuera: para cualquier OTRA instancia ya pinneada
  //    (isPinnedOpen), si el click cayo fuera de su trigger y de su
  //    panel, se cierra -- sin cambios respecto de la ronda anterior.
  //
  // Nunca usa preventDefault/stopPropagation: un click en un link real
  // del panel sigue navegando con normalidad, y target.closest/contains
  // ya resuelven correctamente donde cayo el click.
  document.addEventListener("click", function (event) {
    var clickedTrigger = event.target.closest && event.target.closest(".pm-header-contact__trigger");
    var clickedRoot = clickedTrigger && clickedTrigger.closest("[data-pm-header-contact]");

    if (clickedRoot) {
      initInstance(clickedRoot);
    }

    instances.forEach(function (instance) {
      if (instance.root === clickedRoot) {
        if (instance.isPinnedOpen) {
          instance.isPinnedOpen = false;
          instance.close();
        } else {
          instance.isPinnedOpen = true;
          instance.open();
        }
        return;
      }

      if (!instance.isPinnedOpen) return;
      if (instance.root.contains(event.target) || instance.panel.contains(event.target)) return;
      instance.isPinnedOpen = false;
      instance.close();
    });
  });

  function initAll(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll("[data-pm-header-contact]").forEach(initInstance);
    scheduleSyncAll();
  }

  initAll();

  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });
})();
